/**
 * EditableDataTable
 * A spreadsheet-like grid where every cell is editable.
 * Supports drag-to-reorder for both rows and columns.
 *
 * Props:
 *   grid: string[][] — grid[0] is the header row; grid[1..] are data rows
 *   onChange: (newGrid: string[][]) => void
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Scan data columns for non-numeric contamination.
 * A column is "contaminated" if >40% of its values start with a digit
 * but at least one value contains non-numeric characters (e.g. "$", "%", ",").
 */
function detectContaminatedColumns(grid) {
  if (!grid || grid.length < 2) return [];
  const headers = grid[0];
  const dataRows = grid.slice(1);
  const results = [];

  for (let c = 0; c < headers.length; c++) {
    let digitStart = 0;
    let contaminated = 0;
    let nonEmpty = 0;
    const examples = [];

    for (let r = 0; r < dataRows.length; r++) {
      const val = String(dataRows[r][c] || '').trim();
      if (!val) continue;
      nonEmpty++;
      if (/^[\d$€£¥\-+]/.test(val)) digitStart++;
      if (/\d/.test(val) && /[^0-9.eE\-+\s]/.test(val)) {
        contaminated++;
        if (examples.length < 2) examples.push(val);
      }
    }

    if (nonEmpty >= 2 && digitStart / nonEmpty > 0.4 && contaminated > 0) {
      results.push({ colIdx: c, header: headers[c], contaminated, total: nonEmpty, examples });
    }
  }

  return results;
}

function EditableDataTable({ grid: initialGrid, onChange }) {
  const [grid, setGrid] = useState(initialGrid);
  const { resolved: theme } = useTheme();
  const ui = theme.ui || {};

  const dragRowIdx = useRef(null);
  const dragColIdx = useRef(null);
  const [dropRowTarget, setDropRowTarget] = useState(null);
  const [dropColTarget, setDropColTarget] = useState(null);
  const [columnMenuOpen, setColumnMenuOpen] = useState(null);
  const [dismissedCleanBanner, setDismissedCleanBanner] = useState(false);
  const columnMenuRef = useRef(null);

  const contaminatedCols = useMemo(() => detectContaminatedColumns(grid), [grid]);
  const contaminatedColSet = useMemo(() => new Set(contaminatedCols.map(c => c.colIdx)), [contaminatedCols]);

  useEffect(() => {
    if (columnMenuOpen === null) return;
    const handleClickOutside = (e) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target)) {
        setColumnMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnMenuOpen]);

  const update = useCallback((newGrid) => {
    setGrid(newGrid);
    onChange?.(newGrid);
  }, [onChange]);

  const numRows = grid.length;
  const numCols = grid[0]?.length || 0;

  const handleCellChange = (rowIdx, colIdx, value) => {
    const newGrid = grid.map(row => [...row]);
    newGrid[rowIdx][colIdx] = value;
    update(newGrid);
  };

  const addRow = () => {
    update([...grid, Array(numCols).fill('')]);
  };

  const removeRow = (rowIdx) => {
    if (numRows <= 2) return;
    update(grid.filter((_, i) => i !== rowIdx));
  };

  const addColumn = () => {
    update(grid.map((row, i) => [...row, i === 0 ? `Column ${numCols + 1}` : '']));
  };

  const removeColumn = (colIdx) => {
    if (numCols <= 1) return;
    update(grid.map(row => row.filter((_, i) => i !== colIdx)));
    setColumnMenuOpen(null);
  };

  const stripNonNumeric = (colIdx) => {
    const newGrid = grid.map((row, ri) => {
      if (ri === 0) return [...row];
      const newRow = [...row];
      newRow[colIdx] = newRow[colIdx].replace(/[^0-9.eE+-]/g, '');
      return newRow;
    });
    update(newGrid);
    setColumnMenuOpen(null);
  };

  const promoteRowToHeader = (rowIdx) => {
    if (rowIdx < 1) return;
    const newGrid = grid.map(row => [...row]);
    const rowData = newGrid[rowIdx];
    for (let c = 0; c < numCols; c++) {
      newGrid[0][c] = rowData[c] || newGrid[0][c];
    }
    newGrid.splice(rowIdx, 1);
    update(newGrid);
  };

  const removeEmptyRows = () => {
    const newGrid = grid.filter((row, i) =>
      i === 0 || row.some(cell => String(cell).trim() !== '')
    );
    if (newGrid.length < 2) return;
    update(newGrid);
  };

  const hasEmptyRows = grid.slice(1).some(row => row.every(cell => String(cell).trim() === ''));

  // --- Row drag-and-drop ---
  const handleRowDragStart = (rowIdx) => {
    dragRowIdx.current = rowIdx;
  };

  const handleRowDragOver = (e, rowIdx) => {
    e.preventDefault();
    if (dragRowIdx.current == null || dragRowIdx.current === rowIdx) {
      setDropRowTarget(null);
      return;
    }
    setDropRowTarget(rowIdx);
  };

  const handleRowDrop = (e, targetIdx) => {
    e.preventDefault();
    const srcIdx = dragRowIdx.current;
    if (srcIdx == null || srcIdx === targetIdx) return;

    const newGrid = [...grid];
    const [moved] = newGrid.splice(srcIdx, 1);
    newGrid.splice(targetIdx, 0, moved);
    update(newGrid);
    dragRowIdx.current = null;
    setDropRowTarget(null);
  };

  const handleRowDragEnd = () => {
    dragRowIdx.current = null;
    setDropRowTarget(null);
  };

  // --- Column drag-and-drop ---
  const handleColDragStart = (e, colIdx) => {
    dragColIdx.current = colIdx;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColDragOver = (e, colIdx) => {
    e.preventDefault();
    if (dragColIdx.current == null || dragColIdx.current === colIdx) {
      setDropColTarget(null);
      return;
    }
    setDropColTarget(colIdx);
  };

  const handleColDrop = (e, targetIdx) => {
    e.preventDefault();
    const srcIdx = dragColIdx.current;
    if (srcIdx == null || srcIdx === targetIdx) return;

    const newGrid = grid.map(row => {
      const newRow = [...row];
      const [moved] = newRow.splice(srcIdx, 1);
      newRow.splice(targetIdx, 0, moved);
      return newRow;
    });
    update(newGrid);
    dragColIdx.current = null;
    setDropColTarget(null);
  };

  const handleColDragEnd = () => {
    dragColIdx.current = null;
    setDropColTarget(null);
  };

  const handlePaste = useCallback((e) => {
    const text = e.clipboardData.getData('text');
    if (!text.trim()) return;

    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return;

    e.preventDefault();
    const parsed = lines.map(line => {
      const cells = line.includes('\t') ? line.split('\t') : line.split(',');
      return cells.map(c => c.trim().replace(/^"|"$/g, ''));
    });
    update(parsed);
  }, [update]);

  const inputBase = 'w-full bg-transparent text-xs outline-none px-1 py-0.5';
  const headerInput = `${inputBase} font-medium`;

  const dragHandleStyle = {
    cursor: 'grab',
    color: ui.textSecondary,
    userSelect: 'none',
    fontSize: '10px',
    lineHeight: 1,
    letterSpacing: '1px',
  };

  return (
    <div className="overflow-x-auto">
      {/* Proactive non-numeric contamination banner */}
      {contaminatedCols.length > 0 && !dismissedCleanBanner && (
        <div
          className="border px-3 py-2.5 text-xs leading-relaxed mb-2"
          style={{ backgroundColor: '#fffbeb', borderColor: '#fcd34d', color: '#92400e', borderRadius: 4 }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="font-semibold">Non-numeric characters detected</span>
              <span className="ml-1" style={{ color: '#b45309' }}>
                — {contaminatedCols.length === 1 ? '1 column contains' : `${contaminatedCols.length} columns contain`} characters like $, %, or commas that will prevent numeric parsing.
              </span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {contaminatedCols.map((col) => (
                  <button
                    key={col.colIdx}
                    onClick={() => { stripNonNumeric(col.colIdx); }}
                    className="inline-flex items-center gap-1 border px-2 py-1 text-[11px] font-medium transition-colors"
                    style={{ borderColor: '#f59e0b', color: '#92400e', backgroundColor: '#fef3c7', borderRadius: 3 }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fde68a'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fef3c7'; }}
                    title={`Strip non-numeric characters from "${col.header}" (e.g. ${col.examples.join(', ')})`}
                  >
                    <span style={{ opacity: 0.6 }}>#</span>
                    Clean "{col.header}"
                    <span style={{ opacity: 0.5, fontSize: 10 }}>({col.examples[0]})</span>
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setDismissedCleanBanner(true)}
              className="flex-shrink-0 text-sm leading-none mt-0.5"
              style={{ color: '#b45309', opacity: 0.5 }}
              title="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <table
        className="text-xs border-collapse"
        style={{ minWidth: '100%' }}
        onPaste={handlePaste}
      >
        <thead>
          <tr>
            {/* Empty corner for row drag handle column */}
            <th
              className="border p-0 w-6"
              style={{ borderColor: ui.border, backgroundColor: ui.bgSecondary }}
            />
            {grid[0]?.map((header, colIdx) => (
              <th
                key={colIdx}
                className="border p-0 min-w-[90px]"
                style={{
                  borderColor: ui.border,
                  backgroundColor: dropColTarget === colIdx ? (ui.accent + '22') : ui.bgSecondary,
                  verticalAlign: 'top',
                  color: ui.text,
                  transition: 'background-color 150ms',
                }}
                draggable
                onDragStart={(e) => handleColDragStart(e, colIdx)}
                onDragOver={(e) => handleColDragOver(e, colIdx)}
                onDrop={(e) => handleColDrop(e, colIdx)}
                onDragEnd={handleColDragEnd}
              >
                <div className="flex items-center">
                  <span
                    className="flex-shrink-0 w-4 flex items-center justify-center opacity-40 hover:opacity-100"
                    style={dragHandleStyle}
                    title="Drag to reorder column"
                  >
                    ⠿
                  </span>
                  {contaminatedColSet.has(colIdx) && (
                    <span
                      className="flex-shrink-0 w-2 h-2 rounded-full mr-0.5"
                      style={{ backgroundColor: '#f59e0b' }}
                      title="Contains non-numeric characters"
                    />
                  )}
                  <input
                    className={headerInput}
                    value={header}
                    onChange={(e) => handleCellChange(0, colIdx, e.target.value)}
                    placeholder={`Col ${colIdx + 1}`}
                    style={{ color: ui.text }}
                    onDragStart={(e) => e.stopPropagation()}
                    draggable={false}
                  />
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setColumnMenuOpen(columnMenuOpen === colIdx ? null : colIdx); }}
                      title="Column actions"
                      className="w-4 h-4 flex items-center justify-center text-[10px] leading-none hover:text-black"
                      style={{ color: contaminatedColSet.has(colIdx) ? '#f59e0b' : ui.textSecondary }}
                    >
                      ▾
                    </button>
                    {columnMenuOpen === colIdx && (
                      <div
                        ref={columnMenuRef}
                        className="absolute right-0 top-full mt-0.5 z-50 border shadow-lg py-1 min-w-[180px]"
                        style={{ backgroundColor: ui.bg, borderColor: ui.border }}
                      >
                        <button
                          onClick={() => stripNonNumeric(colIdx)}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 flex items-center gap-2"
                          style={{
                            color: contaminatedColSet.has(colIdx) ? '#92400e' : ui.text,
                            backgroundColor: contaminatedColSet.has(colIdx) ? '#fffbeb' : undefined,
                            fontWeight: contaminatedColSet.has(colIdx) ? 500 : undefined,
                          }}
                        >
                          <span style={{ opacity: contaminatedColSet.has(colIdx) ? 0.8 : 0.5, fontSize: 11 }}>#</span>
                          Strip non-numeric chars
                          {contaminatedColSet.has(colIdx) && (
                            <span className="ml-auto text-[10px]" style={{ color: '#d97706' }}>recommended</span>
                          )}
                        </button>
                        {numCols > 1 && (
                          <button
                            onClick={() => removeColumn(colIdx)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
                            style={{ color: ui.text }}
                          >
                            <span style={{ opacity: 0.5, fontSize: 11 }}>×</span>
                            Remove column
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </th>
            ))}
            <th
              className="border p-0 w-7"
              style={{ borderColor: ui.border, backgroundColor: ui.bgSecondary }}
            >
              <button
                onClick={addColumn}
                title="Add column"
                className="w-full h-full flex items-center justify-center text-sm hover:text-black"
                style={{ minHeight: 26, color: ui.textSecondary }}
              >
                +
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {grid.slice(1).map((row, dataRowIdx) => {
            const rowIdx = dataRowIdx + 1;
            return (
              <tr
                key={rowIdx}
                className="group"
                style={{
                  backgroundColor: dropRowTarget === rowIdx ? (ui.accent + '22') : undefined,
                  transition: 'background-color 150ms',
                }}
                onDragOver={(e) => handleRowDragOver(e, rowIdx)}
                onDrop={(e) => handleRowDrop(e, rowIdx)}
              >
                {/* Row drag handle + promote action */}
                <td
                  className="border p-0 w-6 text-center"
                  style={{ borderColor: ui.borderSecondary }}
                  draggable
                  onDragStart={() => handleRowDragStart(rowIdx)}
                  onDragEnd={handleRowDragEnd}
                >
                  <div className="flex flex-col items-center justify-center">
                    <span
                      className="inline-flex items-center justify-center w-full opacity-30 group-hover:opacity-100 transition-opacity"
                      style={dragHandleStyle}
                      title="Drag to reorder row"
                    >
                      ⠿
                    </span>
                    <button
                      onClick={() => promoteRowToHeader(rowIdx)}
                      title="Use this row as column headers"
                      className="text-[9px] leading-none opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
                      style={{ color: ui.textSecondary }}
                    >
                      ⬆
                    </button>
                  </div>
                </td>
                {row.map((cell, colIdx) => (
                  <td
                    key={colIdx}
                    className="border p-0"
                    style={{ borderColor: ui.borderSecondary }}
                  >
                    <input
                      className={inputBase}
                      value={cell}
                      onChange={(e) => handleCellChange(rowIdx, colIdx, e.target.value)}
                      placeholder=""
                      style={{ color: ui.text }}
                    />
                  </td>
                ))}
                <td
                  className="border p-0 w-7"
                  style={{ borderColor: ui.borderSecondary }}
                >
                  {numRows > 2 && (
                    <button
                      onClick={() => removeRow(rowIdx)}
                      title="Remove row"
                      className="w-full h-full flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
                      style={{ minHeight: 22, color: ui.textSecondary }}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex gap-2 mt-1">
        <button
          onClick={addRow}
          className="flex-1 text-xs border border-dashed py-1 transition-colors"
          style={{ borderColor: ui.borderSecondary, color: ui.textSecondary }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = ui.border; e.currentTarget.style.color = ui.text; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = ui.borderSecondary; e.currentTarget.style.color = ui.textSecondary; }}
        >
          + Add row
        </button>
        {hasEmptyRows && (
          <button
            onClick={removeEmptyRows}
            className="text-xs border border-dashed py-1 px-3 transition-colors"
            style={{ borderColor: '#fca5a5', color: '#dc2626' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.backgroundColor = '#fef2f2'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            Remove empty rows
          </button>
        )}
      </div>
    </div>
  );
}

export default EditableDataTable;
